import { use, useEffect, useMemo, useState } from "react";
import { Calendar, ChevronLeft, ChevronRight, X } from "lucide-react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { ymdLocal } from "../../../utilities/conversions";

function cls(...xs: Array<string | false | undefined>) { return xs.filter(Boolean).join(" "); }

/**********************
 * QuickPlanPanel – arbeidstid/pauser + slots
 **********************/
function QuickPlanPanel({
    availability,
    setAvailability,
    slotMinutes,
    setSlotMinutes,
    weekend,
    setWeekend,
    currentUser,
    client,
    baseUrl,
    setLoading,
    from,
    setFrom,
    to,
    setTo,
    brFrom,
    setBrFrom,
    brTo,
    setBrTo
}: {
    availability: Record<(typeof DAYS)[number], Record<number, boolean>>;
    setAvailability: (v: any) => void;
    slotMinutes: number;
    setSlotMinutes: (n: number) => void;
    weekend: string,
    setWeekend: (v: string) => void,
    currentUser: any,
    client: any,
    baseUrl: string,
    setLoading: (v: boolean) => void;
    from: string;
    setFrom: (v: string) => void;
    to: string;
    setTo: (v: string) => void;
    brFrom: string;
    setBrFrom: (v: string) => void;
    brTo: string;
    setBrTo: (v: string) => void;
}) {

    const [mode, setMode] = useState<'weekly' | 'monthly'>(() => {
        try { return (localStorage.getItem('advisorPlanMode') as 'weekly' | 'monthly') || 'weekly'; } catch { return 'weekly'; }
    });
    const [days, setDays] = useState<Record<string, boolean>>({ Man: true, Tir: true, Ons: true, Tor: true, Fre: true, Lør: false, Søn: false });

    useEffect(() => { try { localStorage.setItem('advisorPlanMode', mode); } catch { } }, [mode]);

    function pickPreset(a: number, b: number, brA?: number, brB?: number) {
        setFrom(String(a).padStart(2, '0') + ":00");
        setTo(String(b).padStart(2, '0') + ":00");
        if (brA != null) setBrFrom(String(brA).padStart(2, '0') + ":00");
        if (brB != null) setBrTo(String(brB).padStart(2, '0') + ":00");
    }

    const handleUpdateClicked = async () => {
        const result = await Swal.fire({
            title: "Oppdater minutter per slot",
            html: `
                                    <h3 style="margin-bottom: 2rem !important">Alle ledige tider fra i morgen vil bli satt til ${slotMinutes} minutter</h3>
                                    
                                `,
            showCancelButton: true,
            confirmButtonText: "Ja oppdate",
            cancelButtonText: "Close",
            customClass: {
                confirmButton: "swal-confirm-btn",
                cancelButton: "swal-cancel-btn",
            },
        });
        if (result.isConfirmed) {
            try {
                setLoading(true);
                var formData = new FormData();
                formData.append('start', from);
                formData.append('end', to);
                formData.append('interval', String(slotMinutes));
                formData.append('weekend', weekend);
                formData.append('break_start', brFrom);
                formData.append('break_end', brTo);
                formData.append('token', currentUser.token);
                formData.append('client_uid', client?.uid);
    
                const response = await axios.post(`${baseUrl}/updateAgentWorkingHours`, formData);
    
                if (response.data.status === "OK") {
                    Swal.fire({
                        icon: 'success',
                        title: 'Suksess',
                        text: 'Arbeidstiden er oppdatert!',
                    });
                }
    
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        }
    }
    function clearDays() {
        const next = applyHoursToDays(availability, { from: 0, to: 0, days, brFrom: undefined, brTo: undefined });
        setAvailability(next);
    }
    function toggleDay(d: string) { setDays(prev => ({ ...prev, [d]: !prev[d] })); }

    return (
        <div className="rounded-xl border border-purple-100 p-3 bg-gradient-to-br from-white to-purple-50">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:gap-4">
                <div>
                    <label className="block text-xs text-gray-600">Arbeidstid fra</label>
                    <input type="time" step={3600} value={from} onChange={e => setFrom(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Arbeidstid til</label>
                    <input type="time" step={3600} value={to} onChange={e => setTo(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Pause fra</label>
                    <input type="time" step={3600} value={brFrom} onChange={e => setBrFrom(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Pause til</label>
                    <input type="time" step={3600} value={brTo} onChange={e => setBrTo(e.target.value)} className="rounded-lg border px-2 py-1" />
                </div>
                <div>
                    <label className="block text-xs text-gray-600">Slots (min)</label>
                    <div className="flex gap-2">
                        {[15, 30, 45, 60].map(m => (
                            <button key={m} onClick={() => setSlotMinutes(m)} className={cls("rounded-full border px-3 py-1 text-sm", slotMinutes === m ? "bg-purple-600 text-white border-purple-600" : "bg-white text-gray-700 border-gray-300")}>{m}</button>
                        ))}
                    </div>
                </div>
                <div>
                    <div className="flex gap-3 text-sm">
                        <button onClick={handleUpdateClicked} className="rounded-full bg-purple-600 text-white px-4 py-2 text-sm cursor-pointer">Update</button>
                    </div>
                </div>
                {/* <div className="md:ml-auto flex gap-2">
                    <button onClick={applyPlan} className="rounded-full bg-purple-600 text-white px-4 py-2 text-sm">Bruk på valgte dager</button>
                    <button onClick={clearDays} className="rounded-full bg-gray-200 text-gray-800 px-4 py-2 text-sm">Tøm på valgte dager</button>
                </div> */}
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                <select onChange={(e) => setWeekend(e.target.value)} name="" id="">
                    <option selected={weekend === "0"} value="0">Weekend Excluded</option>
                    <option selected={weekend === "1"} value="1">Weekend Included</option>
                </select>
                <div className="ml-auto flex items-center gap-2">
                    <span className="text-xs text-gray-600">Hurtigvalg:</span>
                    <button onClick={() => pickPreset(9, 16, 12, 13)} className="rounded-full border px-3 py-1">09–16 (12–13)</button>
                    <button onClick={() => pickPreset(10, 18, 13, 14)} className="rounded-full border px-3 py-1">10–18 (13–14)</button>
                    <button onClick={() => pickPreset(12, 20, 16, 17)} className="rounded-full border px-3 py-1">12–20 (16–17)</button>
                </div>
            </div>
        </div>
    );
}

/**********************
 * Mini månedsoversikt
 **********************/
function MiniMonth({ value, onChange }: { value: Date; onChange: (d: Date) => void }) {
    const [cursor, setCursor] = useState<Date>(() => new Date(value));
    useEffect(() => { setCursor(new Date(value)); }, [value]);

    const monthLabel = new Intl.DateTimeFormat('nb-NO', { month: 'long', year: 'numeric' }).format(cursor);

    function addMonths(base: Date, delta: number) { const d = new Date(base); d.setMonth(d.getMonth() + delta); return d; }
    function startOfMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth(), 1); }
    function daysInMonth(d: Date) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }
    function isSameDay(a: Date, b: Date) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }

    const first = startOfMonth(cursor);
    const leading = first.getDay(); // 0=Sun..6=Sat
    const total = daysInMonth(cursor);
    const cells: Array<{ date: Date | null, key: string }> = [];
    for (let i = 0; i < leading; i++) cells.push({ date: null, key: `pad-${i}` });
    for (let d = 1; d <= total; d++) cells.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), d), key: `d-${d}` });
    while (cells.length % 7 !== 0) cells.push({ date: null, key: `trail-${cells.length}` });

    const today = new Date();

    return (
        <div className="rounded-2xl bg-white border border-purple-100 p-4 shadow">
            <div className="flex items-center justify-between mb-2">
                <button onClick={() => setCursor(c => addMonths(c, -1))} className="p-1 rounded-full hover:bg-gray-100" aria-label="Forrige måned"><ChevronLeft className="w-4 h-4" /></button>
                <div className="text-sm font-semibold capitalize">{monthLabel}</div>
                <button onClick={() => setCursor(c => addMonths(c, 1))} className="p-1 rounded-full hover:bg-gray-100" aria-label="Neste måned"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 text-[11px] text-gray-600 mb-1">
                {['S', 'M', 'T', 'O', 'T', 'F', 'L'].map((w, i) => (<div key={i} className="py-1 text-center">{w}</div>))}
            </div>
            <div className="grid grid-cols-7 gap-1 text-sm">
                {cells.map(({ date, key }) => {
                    if (!date) return <div key={key} className="h-8" />;
                    const selected = isSameDay(date, value);
                    const isToday = isSameDay(date, today);
                    return (
                        <button key={key} onClick={() => onChange(date)} className={cls("h-8 rounded-md text-center", selected ? "bg-purple-600 text-white" : isToday ? "bg-purple-100 text-purple-800" : "hover:bg-gray-100")}>{date.getDate()}</button>
                    );
                })}
            </div>
        </div>
    );
}

type CalendarCardProps = {
    currentUser: any;
    card: string;
    baseUrl: string;
};

// Shape of each event/slot returned from the API
type AgentEvent = {
    id: string;
    agent_id: string;
    sale_id: string | null;
    date: string;                  // "YYYY-MM-DD"
    customer_uid: string;
    client_id: string;
    title: string;                 // e.g. "Appointment with Nouman"
    slot: string;                  // e.g. "10:00 - 10:45"
    start_date_time: string;       // "YYYY-MM-DD HH:mm:ss"
    end_date_time: string;         // "YYYY-MM-DD HH:mm:ss"
    type: "video" | "chat" | "call" | string;
    status: "pending" | "confirmed" | "cancelled" | string;
    attach_card: string;
    cancelled_by: string | null;
    crone_status: string;
    event_key: string;
    minutes_before_event: string;
    on_call: string;
    paymentintents_id: string | null;
    reference: string | null;
    remainder_via_email: string;
    remainder_via_message: string;
    reminder_before: string;
    sms_link: string;
    error: string | null;
    slot_id: string;
};

const DAYS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"] as const;
const WEEKDAY_NAMES = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"] as const;

// Map JS getDay() (0=Sun) to our DAYS index (0=Mon)
function jsDateToDayName(date: Date): (typeof DAYS)[number] {
    return WEEKDAY_NAMES[date.getDay()] as (typeof DAYS)[number];
}

function getDateForDay(dayName: string, baseDate: Date) {
    const base = new Date(baseDate);
    const targetIndex = DAYS.indexOf(dayName as any); // Mon=0
    const currentIndex = (base.getDay() + 6) % 7; // convert Sun=0 → Mon=0

    const diff = targetIndex - currentIndex;
    base.setDate(base.getDate() + diff);

    return ymdLocal(base);
}

// Normalize status for display
function normalizeStatus(status: string): "Planlagt" | "Fullført" | "Avlyst" {
    const s = status?.toLowerCase();
    if (s === "confirmed") return "Fullført";
    if (s === "cancelled") return "Avlyst";
    return "Planlagt"; // pending / anything else
}

// Normalize type for display
function normalizeType(type: string): string {
    const t = type?.toLowerCase();
    if (t === "video") return "Video";
    if (t === "chat") return "Chat";
    if (t === "call") return "Samtale";
    return type;
}

/**********************
 * Kalender‑hjelpere
 **********************/
function genTimes(startHour = 7, endHour = 22, stepMinutes = 60) {
    const out: string[] = [];
    const totalMin = (endHour - startHour) * 60;
    for (let m = 0; m < totalMin; m += stepMinutes) {
        const abs = startHour * 60 + m;
        const hh = Math.floor(abs / 60);
        const mm = abs % 60;
        out.push(String(hh).padStart(2, "0") + ":" + String(mm).padStart(2, "0"));
    }
    return out;
}
function toHour(v: string, fallback: number) {
    const parts = (v || "").split(":");
    const n = parseInt(parts[0] || "", 10);
    if (Number.isNaN(n)) return fallback;
    return Math.max(0, Math.min(23, n));
}
function applyHoursToDays(
    base: Record<(typeof DAYS)[number], Record<number, boolean>>,
    opts: { from: number; to: number; brFrom?: number; brTo?: number; days: Record<string, boolean> }
) {
    const { from, to, brFrom, brTo, days } = opts;
    const next: Record<(typeof DAYS)[number], Record<number, boolean>> = { ...base };
    (Object.keys(days) as Array<keyof typeof days>).forEach((d) => {
        if (!days[d as string]) return;
        const col: Record<number, boolean> = { ...(next as any)[d as string] };
        for (let h = 7; h <= 22; h++) {
            let on = h >= from && h < to;
            if (on && brFrom != null && brTo != null) {
                if (h >= (brFrom as number) && h < (brTo as number)) on = false;
            }
            col[h] = on;
        }
        (next as any)[d as string] = col;
    });
    return next;
}

export function CalendarCard({ currentUser, card, baseUrl }: CalendarCardProps) {
    const client = useSelector((state) => state.client.client);
    const client_uid = client.uid || "clnt_QI9Jd1m0";
    const [loading, setLoading] = useState(false);

    // Valgt dato + kolonnelås
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
    const [lockToSelectedDay, setLockToSelectedDay] = useState<boolean>(() => {
        try { return localStorage.getItem('advisorLockCols') !== '0'; } catch { return true; }
    });
    useEffect(() => { try { localStorage.setItem('advisorLockCols', lockToSelectedDay ? '1' : '0'); } catch { } }, [lockToSelectedDay]);

    // Slots
    const [slotMinutes, setSlotMinutes] = useState<number>(() => {
        try { return parseInt(localStorage.getItem("advisorSlotMinutes") || "30", 10); } catch { return 30; }
    });

    // Real events from API
    const [agentEvents, setAgentEvents] = useState<AgentEvent[]>([]);
    const [weekend, setWeekend] = useState("0");
    const [from, setFrom] = useState("09:00");
    const [to, setTo] = useState("16:00");
    const [brFrom, setBrFrom] = useState("12:00");
    const [brTo, setBrTo] = useState("13:00");

    const getAgentEvents = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/getAgentEvents`, {
                params: { client_uid, token: currentUser.token }
            });

            if (response.data.status === "OK") {
                setAgentEvents(response.data.data || []);
                if (response.data.working_hour) {
                    setWeekend(response.data.working_hour.weekend);
                    setSlotMinutes(parseInt(response.data.working_hour.interval) || 30);
                    setFrom(response.data.working_hour.start);
                    setTo(response.data.working_hour.end);
                    setBrFrom(response.data.working_hour.break_start);
                    setBrTo(response.data.working_hour.break_end);

                }
            }
        } catch (error) {
            console.error("Error fetching agent events:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!currentUser || !baseUrl || !client_uid) return;
        getAgentEvents();
    }, [currentUser, baseUrl, client_uid]);

    // Tilgjengelighetskalender (state)
    type Avail = Record<(typeof DAYS)[number], Record<number, boolean>>;
    const [availability, setAvailability] = useState<Avail>(() => {
        const base: Avail = { Man: {}, Tir: {}, Ons: {}, Tor: {}, Fre: {}, Lør: {}, Søn: {} } as any;
        for (let d of DAYS) for (let h = 7; h <= 22; h++) (base as any)[d][h] = h >= 9 && h <= 16;
        try { const raw = localStorage.getItem('advisorAvail'); return raw ? JSON.parse(raw) : base; } catch { return base; }
    });

    useEffect(() => { try { localStorage.setItem('advisorAvail', JSON.stringify(availability)); } catch { } }, [availability]);

    // Mark booked slots as unavailable in the availability grid
    // Builds a Set of "DAY-HH" strings for quick lookup in the table render
    const bookedSlotKeys = useMemo(() => {
        const keys = new Set<string>();
        agentEvents.forEach(ev => {
            if (!ev.date || !ev.slot) return;
            const d = new Date(ev.date + "T00:00:00"); // parse date only, avoid TZ shift
            const dayName = jsDateToDayName(d) as string;
            // slot format: "10:00 - 10:45"
            const startHour = toHour(ev.slot.split(" - ")[0] || "", -1);
            if (startHour >= 0) {
                keys.add(`${ev.date}-${startHour}`);
            }
        });
        return keys;
    }, [agentEvents, weekend]);

    // ——— SMOKE TESTS ———
    useEffect(() => {
        try {
            const times = genTimes(7, 8, 60);
            console.assert(times.length === 1 && times[0] === '07:00', 'genTimes basic failed');
            const qtr = genTimes(7, 8, 15);
            console.assert(qtr.length === 4 && qtr[0] === '07:00' && qtr[3] === '07:45', 'genTimes 15‑min failed');
            const iso = '2025-01-01T10:00';
            const plus30 = (function add(iso: string, m: number) { const d = new Date(iso); d.setMinutes(d.getMinutes() + m); const p = (n: number) => String(n).padStart(2, '0'); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; })(iso, 30);
            console.assert(plus30.endsWith('10:30'), 'addMinutesISO smoke failed');
        } catch { }
    }, []);

    // Events for the selected day (right panel)
    const selectedISO = ymdLocal(selectedDate);
    const eventsForSelected = useMemo(() => {
        return [...agentEvents]
            .filter(ev => ev.date === selectedISO)
            .sort((a, b) => (a.start_date_time || "").localeCompare(b.start_date_time || ""));
    }, [agentEvents, selectedISO]);

    const handleCancelAppointmentClick = async (id: string) => {
        const result = await Swal.fire({
            title: "Kanseller konsultasjon",
            html: `
                        <h3 style="margin-bottom: 2rem !important">Er du sikker på at du vil avbestille denne konsultasjonen?</h3>
                        <div style="text-align: left; margin: 0 0 16px; padding: 12px 14px; border-radius: 12px; border: 1px solid #e5e7eb; background: #f9fafb; font-size: 14px; color: #111827;">
                            <strong>Merk:</strong> Når en agent kansellerer en konsultasjon, blir hele det autoriserte beløpet frigitt tilbake til kunden umiddelbart.
                        </div>
                    `,
            showCancelButton: true,
            confirmButtonText: "Kanseller konsultasjon",
            cancelButtonText: "Close",
            customClass: {
                confirmButton: "swal-confirm-btn",
                cancelButton: "swal-cancel-btn",
            },
        });
        if (result.isConfirmed) {
            try {
                setLoading(true);
                let formData = new FormData();
                formData.append("token", currentUser.token);
                formData.append("id", id);
                const response = await axios.post(baseUrl + '/deleteSlot', formData);
                if (response.data.status === "OK") {
                    Swal.fire({
                        title: 'Success',
                        text: response.data.data,
                        icon: 'success',
                        timer: 1500,
                    });
                    // Refresh events after cancellation
                    getAgentEvents();
                } else {
                    Swal.fire({
                        title: response.data.status,
                        text: response.data.data,
                        icon: 'error',
                        showConfirmButton: true,
                    })
                }
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="mt-3 text-gray-600 animate-pulse">Laster inn kalendar...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="grid gap-4 md:grid-cols-12">
            {/* Venstre: hurtigpanel + slot-grid */}
            <div className="md:col-span-9 rounded-2xl bg-white border border-purple-100 p-5 shadow">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-purple-900 flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> Tilgjengelighet
                    </h3>
                    <label className="text-xs text-gray-600 inline-flex items-center gap-2">
                        <input type="checkbox" checked={lockToSelectedDay} onChange={() => setLockToSelectedDay(v => !v)} />
                        Lås kolonner til valgt dag ({WEEKDAY_NAMES[selectedDate.getDay()]})
                    </label>
                </div>

                <QuickPlanPanel
                    availability={availability}
                    setAvailability={setAvailability}
                    slotMinutes={slotMinutes}
                    setSlotMinutes={setSlotMinutes}
                    weekend={weekend}
                    setWeekend={setWeekend}
                    currentUser={currentUser}
                    client={client}
                    baseUrl={baseUrl}
                    setLoading={setLoading}
                    from={from}
                    setFrom={setFrom}
                    to={to}
                    setTo={setTo}
                    brFrom={brFrom}
                    setBrFrom={setBrFrom}
                    brTo={brTo}
                    setBrTo={setBrTo}
                />

                <div className="overflow-auto mt-4">
                    <table className="min-w-[900px] text-sm">
                        <thead>
                            <tr>
                                <th className="p-2 text-left text-gray-600">Tid</th>
                                {DAYS.map(d => {
                                    const disabled = lockToSelectedDay && d !== WEEKDAY_NAMES[selectedDate.getDay()];
                                    return (
                                        <th key={d} className={cls("p-2 text-gray-600", disabled && "opacity-40")}>{d}</th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {genTimes(7, 22, slotMinutes).map(t => (
                                <tr key={t} className="border-t">
                                    <td className="p-2 text-gray-600">{t}</td>
                                    {DAYS.map(d => {
                                        if (weekend === "0" && (d === "Lør" || d === "Søn")) {
                                            //skip 
                                            return <td key={d + "-" + t} className="p-1 bg-gray-50" />;
                                        }
                                        const hh = parseInt(t.slice(0, 2), 10);
                                        const isOn = availability[d]?.[hh] ?? false;
                                        const cellDate = getDateForDay(d, selectedDate);
                                        const isBooked = bookedSlotKeys.has(`${cellDate}-${hh}`);
                                        const disabled = lockToSelectedDay && d !== WEEKDAY_NAMES[selectedDate.getDay()];
                                        return (
                                            <td key={d + "-" + t} className="p-1">
                                                <button
                                                    disabled={disabled || isBooked}
                                                    // onClick={() => setAvailability(av => ({
                                                    //     ...av,
                                                    //     [d]: { ...av[d], [hh]: !av[d]?.[hh] }
                                                    // }))}
                                                    className={cls(
                                                        "w-full rounded-md py-2 text-xs",
                                                        (disabled || isBooked) && "cursor-not-allowed",
                                                        disabled && "opacity-40",
                                                        isBooked
                                                            ? "bg-amber-400 text-white"
                                                            : isOn
                                                                ? "bg-purple-600 text-white"
                                                                : "bg-gray-100 text-gray-600"
                                                    )}
                                                    title={isBooked ? "Booket" : undefined}
                                                >
                                                    {isBooked ? "Booket" : isOn ? "Ledig" : "Stengt"}
                                                </button>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-gray-500 mt-3">Merk: Klikk på en rad toggler hele timen. Endringer lagres automatisk.</p>
            </div>

            {/* Høyre: mini månedskalender + dagens avtaler */}
            <div className="md:col-span-3 space-y-3">
                <MiniMonth value={selectedDate} onChange={setSelectedDate} />
                <div className={card.replace("p-5", "p-4")}>
                    <div className="text-sm font-semibold mb-2">Avtaler {selectedISO}</div>
                    <ul className="space-y-2 text-sm">
                        {eventsForSelected.length === 0 && (
                            <li className="text-gray-500">Ingen avtaler denne dagen.</li>
                        )}
                        {eventsForSelected.map(ev => {
                            const status = normalizeStatus(ev.status);
                            return (
                                <li key={ev.id} className="flex flex-col gap-1 border rounded-xl p-2">
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium truncate">{ev.title}</span>
                                        <span className={cls(
                                            "text-xs px-2 py-0.5 rounded-full shrink-0 ml-1",
                                            status === 'Planlagt' && 'bg-amber-100 text-amber-800',
                                            status === 'Fullført' && 'bg-green-100 text-green-800',
                                            status === 'Avlyst' && 'bg-red-100 text-red-800'
                                        )}>{status}</span>
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                        <span>{ev.slot}</span>
                                        <span className="capitalize bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                                            {normalizeType(ev.type)}
                                        </span>
                                        <button onClick={() => handleCancelAppointmentClick(ev.slot_id)} className="bg-red-600 text-white px-2 py-1 rounded-full">Cancel</button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
}